/* eslint-disable consistent-return */
import { useEffect, useRef, useState, useMemo } from "react";
import { debounce } from "lodash";
import PropTypes from "prop-types";
import { TransitionGroup, CSSTransition } from "react-transition-group";
import IntersectionObserverNode from "@shared/v2/IntersectionObserverNode";
import ErrorBoundary from "@shared/v2/ErrorBoundary";
import TextButton from "@shared/v2/TextButton";
import { CloseButton } from "@shared/v2/Modal/ModalHeader";
import TextInput from "@shared/v2/TextInput";
import Tooltip from "@shared/v2/Tooltip";
import {
  BellRegularV6,
  BellSolidV6,
  MagnifyingGlassSolidV6,
  ThumbtackRegularV6,
  ThumbtackSolidV6,
} from "@shared/v2/Icomoon";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";

import Portal from "@shared/v1/Portal";
import TextToggle from "@shared/v2/TextToggle";
import Loading from "../../../Goals/components/Loader";
import { useTheme, useIsAdmin, useFeatureFlags } from "../../../reducers/layoutReducer/selectors";
import {
  includeFeatureFlaggedKeys,
  BATCH_1_ACTIVITIES,
  ACTIVITIES_ADDRESS_LIST_FF,
  ACTIVITIES_FEED_TEST_FF,
} from "./shared";
import useActivities from "./hooks/useActivities";
import "./styles.css";
import EmptyState from "./EmptyState";
import DropdownSelectMultiAgents from "./DropdownSelectMultiAgents";

const ACTIVITIES_CATEGORIES = [
  { label: "All", value: "all" },
  { label: "AI", value: "ai" },
  { label: "Texts", value: "text" },
  { label: "Forms", value: "requests" },
  { label: "Web", value: "web" },
  { label: "CRM", value: "crm" },
  { label: "Recruiter.ai", value: "recruiter" },
  { label: "Marketer", value: "marketer" },
];

const READ_OPTIONS = [
  { label: "Unread", value: false },
  { label: "Read", value: true },
];

const EMPTY_ACTIVITY_TEXT = {
  all: "You’re up to date on everything",
  text: "No new texts",
  requests: "No new form submissions",
  web: "No new web activity",
  crm: "No new CRM activity",
  ai: "No new AI activity",
  marketer: "No new Marketer activity",
  recruiter: "No new Recruiter activity",
};

const TACK_WINDOW_WIDTH = 1920;

export const isActivityFeedPinnedOpen = () =>
  JSON.parse(localStorage.getItem("activityFeed:tack") || "false") && window.innerWidth > TACK_WINDOW_WIDTH;

const ActivitiesFeed = ({ open: showActivityFeed, onOpenChange }) => {
  const isPlace = useTheme()?.name === "place-theme";
  const [tack, setTack] = useState(JSON.parse(localStorage.getItem("activityFeed:tack") || "false"));
  const [showTack, setShowTack] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const activitiesRef = useRef(null);
  const isAdmin = useIsAdmin();
  const featureFlag = useFeatureFlags();

  const [agents, setAgents] = useState([]);

  useEffect(() => {
    if (!isAdmin || agents.length > 0) return;

    const abortController = new AbortController();

    caseTransformingAxios
      .get(`/api/v4/activity_feed/team_people`, { signal: abortController.signal })
      .then((res) => setAgents(res.data))
      .catch(() => setAgents([]));

    return () => {
      abortController.abort();
    };
  }, [isAdmin]);

  const {
    actions,
    activityGroups,
    loading,
    error,
    unreadCount,
    readFilter,
    category,
    categoryCount,
    selectedAgentsIds,
    setQuery,
    query,
  } = useActivities({
    show: showActivityFeed,
    activitiesRef,
    agents,
  });
  const ThumbtackComponent = tack ? ThumbtackSolidV6 : ThumbtackRegularV6;

  const toggleActivityFeed = () => onOpenChange(!showActivityFeed);

  useEffect(() => {
    if (showActivityFeed) {
      // observe window width
      const handleResize = debounce(() => {
        setWindowWidth(window.innerWidth);
      }, 150);
      window.addEventListener("resize", handleResize);
      handleResize();

      return () => {
        window.removeEventListener("resize", handleResize);
      };
    }
    return () => {};
  }, [showActivityFeed]);

  useEffect(() => {
    setShowTack(windowWidth > TACK_WINDOW_WIDTH);
  }, [windowWidth]);

  useEffect(() => {
    setTack(showTack);
  }, [showTack]);

  const ACTIVITY_MAPPING = useMemo(
    () => ({
      ...BATCH_1_ACTIVITIES,
      ...includeFeatureFlaggedKeys(featureFlag.addressListStatus, ACTIVITIES_ADDRESS_LIST_FF),
      ...includeFeatureFlaggedKeys(featureFlag.activityFeedTest, ACTIVITIES_FEED_TEST_FF),
    }),
    [featureFlag.addressListStatus, featureFlag.activityFeedTest],
  );

  const filteredActivityGroups = useMemo(
    () =>
      activityGroups
        .map((group) => ({
          ...group,
          activities: group.activities.filter(
            (activity) => ACTIVITY_MAPPING[activity.notificationType]?.component,
          ),
        }))
        .filter((group) => group.activities.length > 0),
    [activityGroups, ACTIVITY_MAPPING],
  );

  return (
    <>
      <Tooltip
        // @ts-expect-error props
        innerClassName="!tw-bg-neutral-gray-75"
        arrowClassName="tw-fill-neutral-gray-75"
        trigger={
          <button
            type="button"
            className={`tw-group tw-border-none tw-rounded-full tw-h-[36px] tw-ring-[1px] tw-pl-[12px] tw-pr-[6px] tw-whitespace-nowrap tw-flex tw-gap-[6px] tw-items-center tw-ease-out tw-transition-all hover:tw-duration-150 active:tw-duration-50
          ${
            isPlace
              ? `tw-text-white ${showActivityFeed ? "tw-bg-white/10 tw-ring-theme-primary" : "tw-bg-white/10 tw-ring-transparent hover:tw-ring-white/10 hover:tw-bg-white/20 active:tw-bg-white/30"}`
              : `tw-text-gray-75 ${showActivityFeed ? "tw-bg-brivity-blue-10 tw-ring-theme-primary" : "tw-bg-gray-10 tw-ring-transparent hover:tw-ring-theme-primary active:tw-ring-[2px]"}`
          }
        `}
            onClick={toggleActivityFeed}
          >
            <div className="tw-relative tw-flex">
              <BellRegularV6 size="l" />
              <BellSolidV6
                className={`tw-transition-opacity tw-opacity-0 group-hover:tw-opacity-100 ${showActivityFeed ? "tw-opacity-100" : ""} tw-absolute tw-top-0 tw-left-0`}
                size="l"
              />
            </div>
            <div
              className={`tw-bg-[linear-gradient(180deg,#3270FA_0%,#1E4394_100%)] tw-rounded-full tw-bg-theme-primary tw-w-[24px] tw-h-[24px] tw-flex tw-items-center tw-justify-center tw-text-white ${unreadCount > 99 ? "tw-text-[10px]" : "tw-text-12d"}`}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </div>
          </button>
        }
        content="Activity Feed"
        placement="bottom"
      />
      <Portal portalId="activities-portal">
        <div
          className={`tw-max-w-[100vw] tw-w-[564px] tw-absolute tw-z-[100] tw-shadow-[0px_6px_20px_2px_#33333329] tw-bg-white tw-flex tw-flex-col tw-transform tw-transition-transform tw-duration-[500ms] tw-ease-in-out tw-right-0 ${
            tack ? "!tw-relative" : "tw-h-[calc(100%-8px)]"
          } ${showActivityFeed ? "tw-translate-x-0" : "tw-translate-x-full"} ${
            !tack || showActivityFeed ? "" : "tw-ml-[-564px]"
          }`}
        >
          <div className="tw-flex tw-flex-col tw-gap-24px tw-p-[24px] tw-border-solid tw-border-0 tw-border-b tw-border-gray-10 tw-">
            <div className="tw-flex tw-justify-between">
              <span className="tw-text-18d tw-text-gray-base">Activity Feed</span>
              <div className="tw-flex tw-items-center tw-gap-8px">
                {showTack && (
                  <ThumbtackComponent
                    size="l"
                    onClick={() =>
                      setTack((t) => {
                        const newTack = !t;
                        localStorage.setItem("activityFeed:tack", JSON.stringify(newTack));
                        return newTack;
                      })
                    }
                    className="tw-cursor-pointer"
                  />
                )}
                <CloseButton onClick={toggleActivityFeed} />
              </div>
            </div>
            {isAdmin && (
              <div className="tw-mb-[-8px]">
                <DropdownSelectMultiAgents
                  selectedAgentsIds={selectedAgentsIds}
                  setSelectedAgents={actions.setRecipientUuids}
                  agents={agents}
                />
              </div>
            )}
            <TextToggle
              options={ACTIVITIES_CATEGORIES.map((option) => ({
                ...option,
                label: (
                  <div className="tw-flex tw-gap-6px tw-items-center">
                    <span>{option.label}</span>
                    <div className="tw-flex tw-items-center tw-justify-center tw-w-18px tw-h-18px tw-rounded-full tw-bg-theme-primary tw-font-bold tw-text-white tw-text-[9px]">
                      {categoryCount[option.value] > 99 ? "99+" : categoryCount[option.value]}
                    </div>
                  </div>
                ),
              }))}
              value={category}
              size="small"
              onChange={(e) => actions.setCategory(e.target.value)}
              className="!tw-border-0"
              containerClassName="tw-gap-4px !tw-justify-start !tw-h-[initial] tw-flex-wrap"
              labelClassName={`!tw-rounded-full !tw-px-[12px] !tw-border-gray-15  !tw-text-gray-75 tw-transition-all tw-ease-out hover:tw-duration-[150ms] active:tw-duration-[50ms]
                "hover:!tw-text-[#2E6CE5] hover:!tw-border-[#578EFA]
                active:!tw-text-[#2454B2] active:!tw-border-[#578EFA] active:!tw-bg-[#C8D9FA]
                peer-checked:!tw-text-[#2454B2] peer-checked:!tw-border-[#578EFA] peer-checked:tw-bg-[#E6EDFA] !tw-pr-[3px]
                ${isPlace ? "hover:!tw-text-[#2E6CE5] hover:!tw-border-[#578EFA] active:!tw-text-[#2454B2] active:!tw-border-[#578EFA] active:!tw-bg-[#C8D9FA] peer-checked:!tw-text-[#2454B2] peer-checked:!tw-border-[#578EFA] peer-checked:tw-bg-[#E6EDFA]" : "hover:!tw-text-[#4CA7A7] hover:!tw-border-[#94DCDC] active:!tw-text-[#336E7B] active:!tw-border-[#94DCDC] active:!tw-bg-[#DEF3F3] peer-checked:!tw-text-[#336E7B] peer-checked:!tw-border-[#94DCDC] peer-checked:tw-bg-[#EEF9F9]"}
              `}
            />

            <div className="tw-flex tw-gap-24px tw-items-center">
              <TextToggle
                options={READ_OPTIONS}
                value={readFilter}
                size="small"
                onChange={actions.toggleReadFilter}
              />
              <TextButton onClick={actions.markAllAsRead} className="tw-ml-auto" disabled={readFilter}>
                Mark All As Read
              </TextButton>
            </div>
            <div className="tw-flex tw-flex-row tw-relative tw-flex-1">
              <TextInput
                containerClassName="tw-flex-1 tw-w"
                className="tw-pr-32px"
                placeholder="Search activity"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <MagnifyingGlassSolidV6
                size="l"
                className="tw-text-gray-30 tw-absolute tw-right-8px tw-top-10px"
              />
            </div>
          </div>
          <div className="tw-relative tw-flex-1 tw-flex tw-flex-col tw-overflow-hidden">
            {loading && <Loading />}
            <div
              ref={activitiesRef}
              className="tw-w-full tw-flex-1 tw-overflow-y-auto tw-bg-[#F9FBFB] tw-py-16px tw-px-24px"
              onScroll={() => {
                const { scrollTop } = activitiesRef.current;
                actions.setScrolledTop(scrollTop);
              }}
            >
              <TransitionGroup className="tw-flex tw-flex-col tw-gap-16px">
                <EmptyState
                  show={(filteredActivityGroups.length === 0 && !loading) || error}
                  containerClassName="tw-mt-50px"
                >
                  <span className="tw-text-gray-50">{error || EMPTY_ACTIVITY_TEXT[category]}</span>
                </EmptyState>

                {filteredActivityGroups.map(({ date, activities, dateRef }) => (
                  <CSSTransition key={date} nodeRef={dateRef} timeout={300} classNames="date-groupz">
                    <div ref={dateRef}>
                      <div className="tw-text-14d tw-text-gray-50 tw-font-semibold tw-mb-16px">{date}</div>
                      <TransitionGroup className="tw-flex tw-flex-col tw-gap-8px">
                        {activities.map((activity) => {
                          const Activity = ACTIVITY_MAPPING[activity.notificationType]?.component;

                          if (!Activity) return null;

                          return (
                            <CSSTransition
                              key={activity.id}
                              nodeRef={activity.ref}
                              timeout={300}
                              classNames="itemz"
                            >
                              <div ref={activity.ref}>
                                <ErrorBoundary key={activity.id}>
                                  <Activity
                                    activity={activity}
                                    onReadChange={() => actions.onReadChange(activity)}
                                  />
                                </ErrorBoundary>
                              </div>
                            </CSSTransition>
                          );
                        })}
                      </TransitionGroup>
                    </div>
                  </CSSTransition>
                ))}
              </TransitionGroup>
              {filteredActivityGroups.length > 0 && !loading && (
                <IntersectionObserverNode onIntersect={() => actions.loadMore()} />
              )}
            </div>
          </div>
        </div>
      </Portal>
    </>
  );
};

ActivitiesFeed.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
};

export default ActivitiesFeed;
